import { createFileRoute, Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Droplets,
  Utensils,
  Scale,
  FlaskConical,
  TrendingUp,
  ChevronRight,
  CheckCircle2,
  Circle,
  Clock,
} from 'lucide-react';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/apiErrors';
import { useMembership } from '@/hooks/useMembership';
import { PondNotInFarm } from '@/components/pond';
import { isPondNotInFarm } from '@/hooks/usePondElsewhere';
import { useIsPhone } from '@/hooks/useIsPhone';
import { cropPhase } from '@/lib/crop';
import { PhonePrep } from '@/components/entry/phone/PhonePrep';
import { usePondsForDate } from '@/components/entry/adaptive/entryData';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/cn';
import { format, parse } from 'date-fns';
import { todayInPH } from '@/lib/phDate';
import { qk } from '@/lib/queryKeys';
import type { ApiResponse } from '@/types/api';
import type { PondDetailResponse } from '@/types/generated/openapi';

interface PondEntrySearch {
  date?: string;
  /** `1` opens a preparing pond's water-check screen straight away (phone Today shortcut). */
  check?: '1';
}

export const Route = createFileRoute('/_app/entry/pond/$pondId')({
  validateSearch: (search: Record<string, unknown>): PondEntrySearch => {
    return {
      date: typeof search.date === 'string' ? search.date : undefined,
      check: search.check === '1' || search.check === 1 ? '1' : undefined,
    };
  },
  component: PondEntryLayout,
});

// BUG-11: per-pond entry routes. A pre-interpolated string `to` with a query
// (e.g. `/entry/pond/<id>/feeding?date=...`) misrouted feeding/harvest to the
// GLOBAL list pages. Use TanStack's typed form (literal `to` + params + search).
type EntryCardRoute =
  | '/entry/pond/$pondId/water'
  | '/entry/pond/$pondId/feeding'
  | '/entry/pond/$pondId/sampling'
  | '/entry/pond/$pondId/application'
  | '/entry/pond/$pondId/harvest';

interface EntryTypeCardProps {
  name: string;
  icon: React.ElementType;
  /** Absent when the server has no reliable status for this entry type: no badge. */
  status?: 'complete' | 'partial' | 'pending' | 'not_due';
  statusLabel?: string;
  to: EntryCardRoute;
  pondId: string;
  date: string;
  description: string;
  disabled?: boolean;
}

function EntryTypeCard({ name, icon: Icon, status, statusLabel, to, pondId, date, description, disabled }: EntryTypeCardProps) {
  const statusConfig = {
    complete: { badge: 'ok' as const, label: 'Complete', icon: CheckCircle2 },
    partial: { badge: 'warning' as const, label: 'In Progress', icon: Clock },
    pending: { badge: 'neutral' as const, label: 'Pending', icon: Circle },
    not_due: { badge: 'neutral' as const, label: 'Not Due', icon: Circle },
  };

  const config = status ? statusConfig[status] : null;

  const content = (
    <div
      className={cn(
        'relative overflow-hidden rounded-xl border bg-white p-5 transition-all duration-200',
        !disabled && 'hover:shadow-lg hover:shadow-gray-900/10 hover:border-gray-300 hover:-translate-y-0.5 cursor-pointer',
        disabled && 'opacity-60 cursor-not-allowed',
        status === 'complete' && 'border-emerald-200 bg-gradient-to-br from-white to-emerald-50/30'
      )}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <div
            className={cn(
              'p-3 rounded-xl',
              status === 'complete' ? 'bg-emerald-100' : 'bg-gray-100'
            )}
          >
            <Icon
              className={cn(
                'w-6 h-6',
                status === 'complete' ? 'text-emerald-600' : 'text-gray-600'
              )}
            />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{name}</h3>
            <p className="text-sm text-gray-500 mt-0.5">{description}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {config && (
            <Badge variant={config.badge} size="sm" dot>
              {statusLabel ?? config.label}
            </Badge>
          )}
          {!disabled && <ChevronRight className="w-5 h-5 text-gray-400" />}
        </div>
      </div>
    </div>
  );

  if (disabled) {
    return content;
  }

  return (
    <Link to={to} params={{ pondId }} search={{ date }} className="block">
      {content}
    </Link>
  );
}

function PondEntryLayout() {
  const { pondId } = Route.useParams();
  const { date, check } = Route.useSearch();
  const navigate = useNavigate();
  const { membership, isLoading: membershipLoading } = useMembership();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const isPhone = useIsPhone();

  const dateStr = date || todayInPH();
  const displayDate = parse(dateStr, 'yyyy-MM-dd', new Date());

  const basePath = `/entry/pond/${pondId}`;
  const isIndex = pathname === basePath || pathname === `${basePath}/`;

  // Always loaded, not just on the index: it is the same cache entry the sub-pages read, and
  // a pond that is not in this farm must stop every sub-page from rendering.
  const {
    data: pondData,
    isLoading: pondLoading,
    error: pondError,
    fetchStatus: pondFetchStatus,
    refetch: refetchPond,
  } = useQuery({
    queryKey: qk.pond.detail(membership?.farmId, pondId),
    queryFn: async () => {
      const response = await api.get<ApiResponse<PondDetailResponse>>(
        `/api/v1/farms/${membership?.farmId}/ponds/${pondId}/detail`
      );
      return response.data;
    },
    enabled: !!pondId && !!membership?.farmId,
  });

  const { data: checklist } = usePondsForDate(isIndex ? membership?.farmId : undefined, dateStr);
  const entryStatus = checklist?.ponds.find((p) => p.id === pondId);

  // Same key and shape as the harvest page, so a harvest saved there shows here without a refetch.
  const harvestCycleId = pondData?.active_cycle?.id;
  const { data: dayHarvests } = useQuery({
    queryKey: ['harvests', harvestCycleId, dateStr],
    queryFn: async () => {
      const response = await api.get<{ data: unknown[] }>(`/api/v1/farms/${membership?.farmId}/harvests`, {
        params: { cycle_id: harvestCycleId!, start_date: dateStr, end_date: dateStr },
      });
      return response.data || [];
    },
    enabled: isIndex && !!harvestCycleId && !!membership?.farmId,
  });

  if (isPondNotInFarm(pondError)) {
    return <PondNotInFarm pondId={pondId} backTo="/entry" backLabel="Back to Today" />;
  }

  // Offline the request is paused, not failed: no error yet, not loading either. It resumes
  // by itself on reconnect.
  if (pondFetchStatus === 'paused' && !pondData && !membershipLoading) {
    return (
      <div className="max-w-md mx-auto mt-16 text-center" role="status">
        <p className="text-gray-700">No connection. This pond will load when you are back online.</p>
      </div>
    );
  }

  // A 401 can't be fixed by trying again: the layout's session-ended notice carries the
  // sign-in action, so the page itself stays empty rather than offering a Retry that can't work.
  if (pondError instanceof ApiError && pondError.status === 401 && !pondData) {
    return null;
  }

  // A failed request is not "no active crop" or "Pond not found". Rendering the sub-page
  // here would also remount its pond query on every attempt and loop on the spinner.
  if (pondError && !pondData && !membershipLoading && !pondLoading) {
    return (
      <div className="max-w-md mx-auto mt-16 text-center space-y-4" role="alert">
        <p className="text-gray-700">Couldn't load this pond. Try again.</p>
        <button
          type="button"
          onClick={() => void refetchPond()}
          className="min-h-[44px] rounded-lg bg-primary-600 px-4 text-sm font-semibold text-white hover:bg-primary-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!isIndex) {
    if (membershipLoading || pondLoading) {
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <LoadingSpinner size={32} />
        </div>
      );
    }
    return <Outlet />;
  }

  const pond = pondData;
  const cycleId = pond?.active_cycle?.id;

  if (membershipLoading || pondLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size={32} />
      </div>
    );
  }

  if (!pond) {
    return (
      <div className="text-center py-12">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Pond not found</h2>
        <p className="text-gray-500 mb-4">The pond you're looking for doesn't exist.</p>
        <Link to="/entry" className="text-primary-600 hover:text-primary-700 font-medium">
          Back to checklist
        </Link>
      </div>
    );
  }

  // Phone: a preparing pond opens its prep screen. Entry isn't gated by pond
  // assignment anywhere (data input first; the API doesn't enforce it).
  if (isPhone && membership?.farmId && pond.selected_cycle && cropPhase(pond.selected_cycle) === 'prep') {
    return (
      <PhonePrep
        farmId={membership.farmId}
        pondId={pond.id}
        pondName={pond.name}
        pond={pond}
        crop={pond.selected_cycle}
        startWithCheck={check === '1'}
      />
    );
  }

  const isPrep = !!pond.selected_cycle && cropPhase(pond.selected_cycle) === 'prep';

  const entryTypes: Array<{
    id: string;
    name: string;
    icon: React.ElementType;
    status?: 'complete' | 'partial' | 'pending' | 'not_due';
    statusLabel?: string;
    to: EntryCardRoute;
    description: string;
  }> = [
    {
      id: 'water',
      name: 'Water Readings',
      icon: Droplets,
      status: entryStatus?.completion.water,
      to: '/entry/pond/$pondId/water',
      description: 'Record pH, DO, temperature, and other parameters',
    },
    {
      id: 'feeding',
      name: 'Feeding',
      icon: Utensils,
      status: entryStatus?.completion.feeding,
      // "complete" here means at least one feeding row exists, not all slots fed.
      statusLabel: entryStatus?.completion.feeding === 'complete' ? 'Logged' : undefined,
      to: '/entry/pond/$pondId/feeding',
      description: 'Log daily feeding amounts by time slot',
    },
    {
      id: 'sampling',
      name: 'Sampling',
      icon: Scale,
      status: entryStatus?.completion.sampling,
      to: '/entry/pond/$pondId/sampling',
      description: 'Record sample weights and calculate ABW',
    },
    {
      id: 'applications',
      name: 'Applications',
      icon: FlaskConical,
      status: entryStatus && entryStatus.applicationsCount > 0 ? 'complete' : undefined,
      statusLabel: entryStatus ? `${entryStatus.applicationsCount} logged` : undefined,
      to: '/entry/pond/$pondId/application',
      description: 'Log probiotics, treatments, and supplements',
    },
    {
      id: 'harvest',
      name: 'Harvests',
      icon: TrendingUp,
      status: dayHarvests && dayHarvests.length > 0 ? 'complete' : undefined,
      statusLabel: dayHarvests && dayHarvests.length > 0 ? `${dayHarvests.length} recorded` : undefined,
      to: '/entry/pond/$pondId/harvest',
      description: 'Record partial or final harvest data',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Back button and header */}
      <div className="flex items-start gap-4">
        <button
          onClick={() => navigate({ to: '/entry', search: { date: dateStr } })}
          className="p-2 -ml-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold text-gray-900 truncate">{pond.name}</h1>
          <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
            {pond.active_cycle && (
              <span>
                DOC <span className="font-medium text-gray-700">{pond.active_cycle.doc}</span>
              </span>
            )}
            <span className="text-gray-300">•</span>
            <span>{format(displayDate, 'EEEE, MMMM d')}</span>
          </div>
        </div>
      </div>

      {/* Entry type cards */}
      {isPrep ? (
        <div className="space-y-3">
          <h2 className="font-semibold text-gray-900">Data Entry</h2>
          <p className="text-sm text-gray-600">
            This pond is being prepared. Daily readings, feeding, sampling and harvests start once it is stocked.
            Until then, log water checks and applications on its Pre-stocking tab.
          </p>
          <div className="grid gap-3">
            {[
              { name: '+ Water check', icon: Droplets, description: 'pH, salinity, transparency and other checks during preparation' },
              { name: '+ Application', icon: FlaskConical, description: 'Lime, fertilizer, probiotics and other inputs during preparation' },
            ].map(({ name, icon: Icon, description }) => (
              <Link
                key={name}
                to="/ponds/$pondId"
                params={{ pondId }}
                search={(prev) => ({ ...prev, tab: 'prestocking' })}
                className="block rounded-xl border bg-white p-5 transition-all duration-200 hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-lg hover:shadow-gray-900/10"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="rounded-xl bg-gray-100 p-3">
                      <Icon className="h-6 w-6 text-gray-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{name}</h3>
                      <p className="mt-0.5 text-sm text-gray-500">{description}</p>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 shrink-0 text-gray-400" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <h2 className="font-semibold text-gray-900">Data Entry</h2>
          <div className="grid gap-3">
            {entryTypes.map((type) => (
              <EntryTypeCard
                key={type.id}
                name={type.name}
                icon={type.icon}
                status={type.status}
                statusLabel={type.statusLabel}
                to={type.to}
                pondId={pondId}
                date={dateStr}
                description={type.description}
                disabled={!cycleId}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
